import {Box, Flex, useTheme, VStack, Image, Link} from '@chakra-ui/react';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {OFFER_STEPS} from '..';
import useAuth from '../../../../../../hooks/useAuth';
import {useGeneralSettings} from '../../../../../../hooks/useGeneralSettings';
import {Offer, OfferType, Place, Event} from '../../../../../../types/article';
import {getDirectionsLinkFromPlace} from '../../../../../../utils/address';
import {getOfferDate, isFutureDate} from '../../../../../../utils/date';
import {BlueButton} from '../../../../../button';
import {TextG10, TextG12} from '../../../../../typography';
import dayjs from 'dayjs';
import Icon from '../../../../../Icon';
import {AnchorAttributes, createAnchorWithAttributes} from '../../../../../../utils/externalLink';
import HTMLReactParser from 'html-react-parser';
import {checkIsOfferLocked} from '../../../OfferCard';
import {storeScrollPositionInSession} from '../../../../../../utils/scroll';
import {isFree} from '../../../../../../utils/price';

const RedemptionStepOne = ({
  offer,
  event,
  place,
  bookmarkHandler,
  isBookmarked,
  setShowModal,
  distance,
  setCurStep,
  isFutureOffer,
  datePassed,
  isPreview = false,
}: {
  offer: Offer;
  event?: Event;
  place: Place;
  bookmarkHandler: (e: any) => Promise<string | number | undefined> | undefined;
  isBookmarked: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  distance: Number;
  setCurStep: React.Dispatch<React.SetStateAction<OFFER_STEPS>>;
  isFutureOffer: boolean;
  datePassed: boolean;
  isPreview?: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {getSetting, isLoading} = useGeneralSettings(!isPreview);
  const {user} = useAuth();

  const userPoints = user?.pointsTotal;
  const pointCost = offer.points_redeem_amount; //50000;

  const isProduction = process.env.NODE_ENV === 'production';
  const freedomFestOfferId = isProduction ? '24137' : '30287';
  const isFreedomFestOffer = offer?.id.toString() === freedomFestOfferId;

  const pointDiff = (userPoints || 0) - pointCost;
  const enoughPoints = pointDiff >= 0;
  const unlocksByEventCheckIn = event && event.unlocks_offer_id === offer.id;
  const unlockedByEventCheckIn = event && event.unlocked_offer?.offer_id === offer.id;
  const lockInfo = checkIsOfferLocked(offer.id, event, offer, user);
  const isOfferLocked = lockInfo.isLocked;
  const actionsBar = () => {
    if (isOfferLocked) {
      // For future lock type, use offer start date
      // For event lock type, use whichever start date is further away
      const offerStartDate = offer.starts_at ? dayjs.utc(offer.starts_at) : null;
      const eventStartDate = event?.start_date_time ? dayjs.utc(event.start_date_time) : null;

      // For future offers, always use the offer start date
      let displayDate = offerStartDate;
      if (lockInfo.lockType === 'event') {
        // For event locks, use the furthest date (original behavior)
        if (offerStartDate && eventStartDate) {
          displayDate = offerStartDate.isAfter(eventStartDate) ? offerStartDate : eventStartDate;
        } else {
          displayDate = eventStartDate || offerStartDate;
        }
      }

      const isFuture = displayDate ? isFutureDate(displayDate.toISOString()) : false;
      const date = displayDate ? displayDate.local().format('MMM DD') : '';

      if (lockInfo.lockType === 'future') {
        const offerDate = offerStartDate ? offerStartDate.local().format('MMM DD') : '';
        return (
          <>
            <BlueButton
              cursor="none !important;"
              _focus={{boxShadow: 'none'}}
              _hover={{boxShadow: 'none'}}
              _active={{boxShadow: 'none'}}
              isDisabled={true}
              w="auto">
              <Icon
                style={{color: theme.colors.white, fontSize: '14px'}}
                iconName={'fi fi-sr-lock'}
              />
              <TextG12 ml={2} color={theme.colors.white} fontWeight="500">
                Unlocks on {offerDate}
              </TextG12>
            </BlueButton>
            {!isBookmarked && !datePassed && (
              <TextG12
                onClick={(e) => {
                  if (!isPreview) {
                    setShowModal(false);
                    bookmarkHandler(e);
                  }
                }}
                textDecoration="none"
                fontWeight="400"
                color={theme.colors.grey}>
                Remind Me Later
              </TextG12>
            )}
          </>
        );
      }

      if (lockInfo.lockType === 'membership') {
        return (
          <>
            <BlueButton
              cursor="pointer !important;"
              _focus={{boxShadow: 'none'}}
              _hover={{boxShadow: 'none'}}
              _active={{boxShadow: 'none'}}
              onClick={() => {
                setShowModal(false);
                navigate('/memberships');
              }}
              w="auto">
              <Icon
                style={{color: theme.colors.white, fontSize: '14px'}}
                iconName={'fi fi-sr-crown'}
              />
              <TextG12 ml={2} color={theme.colors.white} fontWeight="500">
                Join Membership To Unlock
              </TextG12>
            </BlueButton>
            {!isBookmarked && !datePassed && (
              <TextG12
                onClick={(e) => {
                  if (!isPreview) {
                    setShowModal(false);
                    bookmarkHandler(e);
                  }
                }}
                textDecoration="none"
                fontWeight="400"
                color={theme.colors.grey}>
                Remind Me Later
              </TextG12>
            )}
          </>
        );
      }

      // Event lock (original behavior)
      return (
        <>
          <BlueButton
            cursor="none !important;"
            _focus={{boxShadow: 'none'}}
            _hover={{boxShadow: 'none'}}
            _active={{boxShadow: 'none'}}
            isDisabled={true}
            w="auto">
            <Icon
              style={{color: theme.colors.white, fontSize: '14px'}}
              iconName={'fi fi-sr-lock'}
            />
            <TextG12 ml={2} color={theme.colors.white} fontWeight="500">
              Unlock with Check-In {isFuture && `on ${date}`}
            </TextG12>
          </BlueButton>
          {!isBookmarked && !datePassed && (
            <TextG12
              onClick={(e) => {
                if (!isPreview) {
                  setShowModal(false);
                  bookmarkHandler(e);
                }
              }}
              textDecoration="none"
              fontWeight="400"
              color={theme.colors.grey}>
              Remind Me Later
            </TextG12>
          )}
        </>
      );
    }

    if (unlockedByEventCheckIn) {
      const isQuantityAvailable = offer?.quantity_available > 0;
      const isAlreadyUsed = !!(offer?.readable_status && offer.readable_status !== 'Available');
      return (
        <>
          <BlueButton
            cursor="none !important;"
            _focus={{boxShadow: 'none'}}
            _hover={{boxShadow: 'none'}}
            _active={{boxShadow: 'none'}}
            isDisabled={!enoughPoints || datePassed || isFutureOffer || !isQuantityAvailable || isAlreadyUsed}
            onClick={() => (!isPreview ? setCurStep(OFFER_STEPS.Confirm) : null)}
            w="180px"
            backgroundColor={!isQuantityAvailable || isAlreadyUsed ? theme.colors.grey : undefined}>
            <TextG10 color={theme.colors.white} fontWeight="500">
              {isAlreadyUsed ? 'Already Used' : !isQuantityAvailable ? 'Unavailable' : 'Redeem Now'}
            </TextG10>
          </BlueButton>

          {!isBookmarked && !datePassed && (
            <TextG12
              onClick={(e) => {
                if (!isPreview) {
                  setShowModal(false);
                  bookmarkHandler(e);
                }
              }}
              textDecoration="none"
              fontWeight="400"
              color={theme.colors.grey}>
              Remind Me Later
            </TextG12>
          )}
        </>
      );
    }

    if (isFutureOffer && !isBookmarked) {
      return (
        <BlueButton
          cursor="none !important;"
          _focus={{boxShadow: 'none'}}
          _hover={{boxShadow: 'none'}}
          _active={{boxShadow: 'none'}}
          w="180px"
          onClick={(e) => {
            setShowModal(false);
            bookmarkHandler(e);
          }}
          fontWeight="400"
          color={theme.colors.white}>
          Remind Me
        </BlueButton>
      );
    }

    if (offer.is_geofencing_required && (offer.place || place)) {
      if (getSetting('checkin_distance') && distance < getSetting('checkin_distance')) {
        const isQuantityAvailable = offer?.quantity_available > 0;
        const isAlreadyUsed = !!(offer?.readable_status && offer.readable_status !== 'Available');
        return (
          <>
            <BlueButton
              cursor="none !important;"
              _focus={{boxShadow: 'none'}}
              _hover={{boxShadow: 'none'}}
              _active={{boxShadow: 'none'}}
              isDisabled={!enoughPoints || datePassed || !isQuantityAvailable || isAlreadyUsed}
              onClick={() => (!isPreview ? setCurStep(OFFER_STEPS.Confirm) : null)}
              w="180px"
              backgroundColor={!isQuantityAvailable || isAlreadyUsed ? theme.colors.grey : undefined}>
              <TextG10 color={theme.colors.white} fontWeight="500">
                {isAlreadyUsed ? 'Already Used' : !isQuantityAvailable ? 'Unavailable' : 'Redeem Now'}
              </TextG10>
            </BlueButton>

            {!isBookmarked && !datePassed && (
              <TextG12
                onClick={(e) => {
                  if (!isPreview) {
                    setShowModal(false);
                    bookmarkHandler(e);
                  }
                }}
                textDecoration="none"
                fontWeight="400"
                color={theme.colors.grey}>
                Remind Me Later
              </TextG12>
            )}
          </>
        );
      } else {
        const isQuantityAvailable = offer?.quantity_available > 0;
        return (
          <>
            <BlueButton
              cursor="none !important;"
              _focus={{boxShadow: 'none'}}
              _hover={{boxShadow: 'none'}}
              _active={{boxShadow: 'none'}}
              isDisabled={datePassed}
              w="180px"
              onClick={() => {
                let url = getDirectionsLinkFromPlace(place);
                const attributes: AnchorAttributes = {
                  href: url,
                  target: '_blank',
                  id: 'getDirections',
                };
                const anchor = createAnchorWithAttributes(attributes);
                window.document.body.appendChild(anchor);
                let link = document.getElementById('getDirections');
                link?.click();
                location.href = `/place/${place.id}`;
              }}>
              <TextG12 color={theme.colors.white} fontWeight="500">
                Take Me There!
              </TextG12>
            </BlueButton>

            {/* "I'm Already Here" option to bypass geofencing check */}
            {!datePassed && !isFutureOffer && enoughPoints && isQuantityAvailable && (
              <TextG12
                onClick={() => (!isPreview ? setCurStep(OFFER_STEPS.Confirm) : null)}
                textDecoration="underline"
                fontWeight="500"
                color={theme.colors.blue}
                cursor="pointer">
                I'm already here — redeem now
              </TextG12>
            )}

            {!isBookmarked && !datePassed && (
              <TextG12
                onClick={(e) => {
                  if (!isPreview) {
                    setShowModal(false);
                    bookmarkHandler(e);
                  }
                }}
                textDecoration="none"
                fontWeight="400"
                color={theme.colors.grey}>
                Remind Me Later
              </TextG12>
            )}
          </>
        );
      }
    } else if (isFreedomFestOffer) {
      return null;
    } else {
      const isQuantityAvailable = offer?.quantity_available > 0;
      const isAlreadyUsed = !!(offer?.readable_status && offer.readable_status !== 'Available');
      return (
        <>
          <BlueButton
            cursor="none !important;"
            _focus={{boxShadow: 'none'}}
            _hover={{boxShadow: 'none'}}
            _active={{boxShadow: 'none'}}
            isDisabled={isFutureOffer || !enoughPoints || datePassed || !isQuantityAvailable || isAlreadyUsed}
            onClick={() => (!isPreview ? setCurStep(OFFER_STEPS.Confirm) : null)}
            w="180px"
            backgroundColor={!isQuantityAvailable || isAlreadyUsed ? theme.colors.grey : undefined}>
            <TextG10 color={theme.colors.white} fontWeight="500">
              {isAlreadyUsed ? 'Already Used' : !isQuantityAvailable ? 'Unavailable' : 'Redeem Now'}
            </TextG10>
          </BlueButton>

          {!isBookmarked && !datePassed && (
            <TextG12
              onClick={(e) => {
                if (!isPreview) {
                  setShowModal(false);
                  bookmarkHandler(e);
                }
              }}
              textDecoration="none"
              fontWeight="400"
              color={theme.colors.grey}>
              Remind Me Later
            </TextG12>
          )}
        </>
      );
    }
  };

  const learnMoreUrl = (place: Place, event?: Event) => {
    if (event) {
      return `/event/${event?.id || offer.event_id}`;
    } else if (place) {
      return `/place/${place?.id || offer.place_id}`;
    } else if (offer.partner_id) {
      return `/partner/${offer.partner_id}`;
    } else if (offer.article_id) {
      return `/post/${offer.article_id}`;
    }
  };

  return (
    <>
      <Flex
        pos="relative"
        zIndex={99}
        color={theme.colors.white}
        py={1}
        px={2}
        background={theme.colors.rose}
        borderRadius="10px"
        mt="-30px !important"
        flexDir="column"
        align="center">
        {isOfferLocked && lockInfo.lockType === 'membership' && (
          <Box
            background="rgba(0, 0, 0, 0.5)"
            borderRadius="10px"
            pos="absolute"
            top="0px"
            left="0px"
            height="100%"
            w="100%"></Box>
        )}
        <TextG12 fontWeight="700">Redeem For</TextG12>
        <Box>
          <TextG12 fontWeight="700">{`${pointCost.toLocaleString()} Points`}</TextG12>
        </Box>
      </Flex>
      {userPoints && (
        <TextG12 color={(!enoughPoints && theme.colors.rose) || theme.colors.grey} fontWeight="500">
          {enoughPoints
            ? `Your Balance: ${userPoints?.toLocaleString()}`
            : `You Need: ${Math.abs(pointDiff).toLocaleString()} More Points`}
        </TextG12>
      )}
      {offer?.quantity_available === 0 ? (
        <TextG12 color={theme.colors.rose} fontWeight="500" textAlign="center">
          No more available
        </TextG12>
      ) : (
        offer?.quantity_available !== null &&
        offer.quantity_available <= 99 && (
          <TextG12 fontWeight="500" textAlign="center" color={theme.colors.rose}>
            {offer?.quantity_available} Available
          </TextG12>
        )
      )}
      {offer.offer_type == OfferType.MEMBERSHIP_GET || OfferType.MEMBERSHIP_GIVE ? (
        <></>
      ) : isFutureOffer ? (
        <Flex textAlign="center" py="2px" px={4} align="center" gap="4px">
          <Icon
            iconName="fi-rr-clock-nine"
            style={{
              color: theme.colors.blue,
              marginBottom: '0.25rem',
              fontSize: '18px',
              width: '18px',
              height: '18px',
            }}
          />
          <TextG12
            lineHeight="14px"
            color={theme.colors.blue}
            fontWeight="700"
            isTruncated="&hellip;"
            noOfLines={1}>
            AVAILABLE {getOfferDate(offer.starts_at)?.toUpperCase()}
          </TextG12>
        </Flex>
      ) : (
        <>
          <TextG10
            lineHeight="10px"
            color={theme.colors.lightestGrey}
            py="2px"
            px={4}
            fontWeight="500"
            isTruncated="&hellip;"
            noOfLines={1}>
            {`${datePassed ? 'EXPIRED ON' : 'EXPIRES'} ${getOfferDate(
              offer?.ends_at,
            )?.toUpperCase()}`}
          </TextG10>
          <TextG10
            lineHeight="10px"
            mt="0 !important"
            color={theme.colors.lightestGrey}
            fontWeight="400">
            Limit {offer?.total_use_limit_per_user}x per person
          </TextG10>
        </>
      )}

      {offer.summary && (
        <Box
          py={3}
          px={4}
          textAlign="center"
          border={`1px solid ${theme.colors.lighterGrey}`}
          borderRadius="7px">
          <TextG12 color={theme.colors.grey} align="center" maxW="284px" fontWeight="500">
            {HTMLReactParser(offer?.summary)}
          </TextG12>
          {offer && offer.offer_type === OfferType.EVENT_REDEMPTION && (
            <Flex gap={3} mt={3}>
              <TextG12 color={theme.colors.grey} fontWeight="700">
                {offer?.event?.start_date_time &&
                  dayjs.utc(offer.event.start_date_time).local().format('MMM. D [at] h:mmA')}
              </TextG12>
              <TextG12 color="#B6B6B6">
                {offer.quantity_available}
                {offer.quantity_type ? ` ${offer.quantity_type}` : ``} left
              </TextG12>
            </Flex>
          )}
        </Box>
      )}
      <VStack>{actionsBar()}</VStack>
    </>
  );
};

export default RedemptionStepOne;
